import { useCallback, useRef, useState, type RefObject } from 'react'
import { parseTimeExpression, type TimeExpressionMatch } from '../utils/parse-time-expression'

const MARK_CLASS = 'bg-blue-9/20 rounded-md corner-smooth px-1 mx-px'
const DEBOUNCE_MS = 250

function getCaretOffset(element: HTMLElement): number {
  const selection = window.getSelection()
  if (!selection || selection.rangeCount === 0) return 0

  const range = selection.getRangeAt(0)
  const preRange = document.createRange()
  preRange.selectNodeContents(element)
  preRange.setEnd(range.startContainer, range.startOffset)
  return preRange.toString().length
}

function setCaretOffset(element: HTMLElement, offset: number): void {
  const selection = window.getSelection()
  if (!selection) return

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT)
  let remaining = offset

  while (walker.nextNode()) {
    const node = walker.currentNode as Text
    if (remaining <= node.length) {
      const range = document.createRange()
      range.setStart(node, remaining)
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)
      return
    }
    remaining -= node.length
  }

  // If offset exceeds content, place at end
  const range = document.createRange()
  range.selectNodeContents(element)
  range.collapse(false)
  selection.removeAllRanges()
  selection.addRange(range)
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function buildHighlightedHtml(text: string, match: TimeExpressionMatch): string {
  const before = escapeHtml(text.slice(0, match.startIndex))
  const marked = escapeHtml(text.slice(match.startIndex, match.endIndex))
  const after = escapeHtml(text.slice(match.endIndex))
  return `${before}<mark class="${MARK_CLASS}">${marked}</mark>${after}`
}

export function useTimeExpressionHighlight(inputRef: RefObject<HTMLDivElement | null>) {
  const matchRef = useRef<TimeExpressionMatch | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastHtmlRef = useRef<string>('')
  const dismissedTextRef = useRef<string | null>(null)
  const [hasMatch, setHasMatch] = useState(false)
  const [currentMatch, setCurrentMatch] = useState<TimeExpressionMatch | null>(null)

  const clearHighlight = useCallback(() => {
    const el = inputRef.current
    if (!el) return

    const plainText = el.innerText ?? ''
    if (lastHtmlRef.current !== '') {
      const caretPos = getCaretOffset(el)
      el.textContent = plainText
      lastHtmlRef.current = ''
      setCaretOffset(el, caretPos)
    }
  }, [inputRef])

  const dismissMatch = useCallback(() => {
    if (matchRef.current) {
      dismissedTextRef.current = matchRef.current.text
    }
    matchRef.current = null
    setHasMatch(false)
    setCurrentMatch(null)
    clearHighlight()
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [inputRef, clearHighlight])

  const handleHighlightInput = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      const el = inputRef.current
      if (!el) return

      const plainText = el.innerText ?? ''
      // Normalize: replace \n from <br> with space for matching, but keep original for display
      const normalizedText = plainText.replace(/\n/g, ' ')
      const match = parseTimeExpression(normalizedText)

      // If the user dismissed this exact match text, don't re-highlight
      if (match && dismissedTextRef.current === match.text) {
        matchRef.current = null
        setHasMatch(false)
        setCurrentMatch(null)
        clearHighlight()
        return
      }

      // Text changed enough that the dismissed match no longer applies
      if (dismissedTextRef.current && (!match || dismissedTextRef.current !== match.text)) {
        dismissedTextRef.current = null
      }

      matchRef.current = match
      setHasMatch(match !== null)
      setCurrentMatch(match)

      if (!match) {
        clearHighlight()
        return
      }

      const nextHtml = buildHighlightedHtml(plainText, match)
      if (nextHtml === lastHtmlRef.current) return

      const caretPos = getCaretOffset(el)
      el.innerHTML = nextHtml
      lastHtmlRef.current = nextHtml
      setCaretOffset(el, caretPos)
    }, DEBOUNCE_MS)
  }, [inputRef, clearHighlight])

  return {
    handleHighlightInput,
    dismissMatch,
    hasMatch,
    currentMatch,
  }
}
